import { Link } from 'wouter';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Reveal, fadeUp, stagger } from './helpers';

const tiers = [
    { name: 'Essential', price: '£3,500', fit: 'For specialists, team leads, and anyone whose role takes months to learn.' },
    { name: 'Standard', price: '£5,500', fit: 'For directors and anyone owning a P&L, a key relationship, or a process.', featured: true },
    { name: 'Premium', price: '£9,000', fit: 'For VPs, C-suite, and founder transitions.' },
];

export const PricingSection = () => (
    <section id="pricing" className="py-32 scroll-mt-16 border-t border-border/40">
        <div className="max-w-5xl mx-auto px-6">
            <Reveal className="max-w-2xl mb-14">
                <p className="text-[11px] uppercase tracking-[0.2em] text-primary font-semibold mb-5">Pricing</p>
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.025em] leading-[1.05]">
                    One price per departure.
                    <span className="text-muted-foreground/60"> No seats. No annual lock-in.</span>
                </h2>
                <p className="mt-5 text-lg text-muted-foreground leading-relaxed">You pay when there's actually a departure. Priced by the scope of the role.</p>
            </Reveal>

            <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-80px' }} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {tiers.map((t) => (
                    <motion.div
                        key={t.name}
                        variants={fadeUp}
                        whileHover={{ y: -4 }}
                        transition={{ duration: 0.25 }}
                        className={`rounded-2xl p-7 ${t.featured ? 'bg-primary text-primary-foreground shadow-xl shadow-primary/20' : 'border border-border/60 bg-card'}`}
                    >
                        <div className="text-xs uppercase tracking-widest font-semibold mb-3 opacity-70">{t.name}</div>
                        <div className="text-4xl font-bold tracking-tight tabular-nums">{t.price}</div>
                        <div className={`text-xs mt-1 ${t.featured ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>per departure</div>
                        <div className={`text-sm mt-5 leading-relaxed ${t.featured ? 'text-primary-foreground/85' : 'text-foreground/75'}`}>{t.fit}</div>
                    </motion.div>
                ))}
            </motion.div>

            <Reveal>
                <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-start">
                    <Link href="/pricing">
                        <Button className="gap-2 rounded-full h-11 px-6">
                            What's included <ArrowRight className="h-4 w-4" />
                        </Button>
                    </Link>
                    <Link href="/calculator/advanced">
                        <Button variant="outline" className="gap-2 rounded-full h-11 px-6">
                            Build the business case
                        </Button>
                    </Link>
                </div>
                <p className="text-xs text-muted-foreground mt-6">You pay once the interviews are done. If the notes aren't useful, you don't pay.</p>
            </Reveal>
        </div>
    </section>
);
